// Android タブレットなどの付帯機材。機材台帳の AutoFork と linkedMachineId で紐付ける
export interface TabletInput {
  // 管理名（用途・持ち主など任意のラベル）
  name?: string
  // 型番（例: TB330XU）
  model?: string
  // タイプ（機材台帳と同じ Standard / Lite）
  edition?: string
  serialNumber?: string
  imei?: string
  // メーカーの P/N（例: ZADB0291JP）
  productNumber?: string
  mfgDate?: string
  // 紐付け先 AutoFork（機材台帳の machineId）
  linkedMachineId?: string
  note?: string
}

export interface Tablet extends TabletInput {
  tabletId: string
  createdAt?: string
  updatedAt?: string
  updatedBy?: string
}

export const TABLET_SERIAL_PATTERN = /^[A-Za-z0-9_-]{1,32}$/
export const IMEI_PATTERN = /^\d{14,16}$/

// スキャンで自動入力できる項目（読み取り済み表示にも使う）
export type ScannedField = keyof Pick<
  TabletInput,
  'model' | 'serialNumber' | 'imei' | 'productNumber' | 'mfgDate'
>

export const SCANNED_FIELDS: ScannedField[] = [
  'model',
  'serialNumber',
  'imei',
  'productNumber',
  'mfgDate',
]

// 単体の値（バーコード1本分・QR内の1トークン）をパターンで判別する
function classifyToken(token: string): Partial<Record<ScannedField, string>> {
  if (/^\d{14,16}$/.test(token)) return { imei: token }
  if (/^ZA[A-Z0-9]{8}$/i.test(token)) {
    return { productNumber: token.toUpperCase() }
  }
  if (/^TB\d{3}[A-Z0-9]{0,6}$/i.test(token)) {
    return { model: token.toUpperCase() }
  }
  if (/^20\d{2}-[01]\d-[0-3]\d$/.test(token)) return { mfgDate: token }
  // Lenovo の QR にある "1S" + P/N (ZA + 8桁) + S/N の連結
  const lenovoQr = token.match(/^1S(ZA[A-Z0-9]{8})([A-Z0-9]{6,16})$/i)
  if (lenovoQr) {
    return {
      productNumber: lenovoQr[1].toUpperCase(),
      serialNumber: lenovoQr[2].toUpperCase(),
    }
  }
  // 数字を含む 6〜16 桁の英数字はシリアルとみなす（全数字は IMEI 等と紛らわしいため除外済み）
  if (/^[A-Z0-9]{6,16}$/i.test(token) && /\d/.test(token)) {
    return { serialNumber: token.toUpperCase() }
  }
  return {}
}

// ラベルの QR・バーコードから読み取った文字列を項目へ振り分ける。
// Lenovo ラベルの例では S/N・IMEI は Code128 の単体バーコード、
// QR は IMEI・S/N・P/N のカンマ区切り。区切り文字で分割して各トークンを判別する。
// 判別できなかった場合は空オブジェクトを返す（呼び出し側で無視する）
export function parseScannedCode(
  raw: string
): Partial<Record<ScannedField, string>> {
  const text = raw.trim()
  if (!text) return {}

  // ラベル名付きの値（QR に "IMEI: xxx" のようなテキストで入っている場合）
  const fields: Partial<Record<ScannedField, string>> = {}
  const imeiLabeled = text.match(/IMEI\s*[:#]?\s*(\d{14,16})/i)
  if (imeiLabeled) fields.imei = imeiLabeled[1]
  const snLabeled = text.match(/S\/?N\s*[:#]?\s*([A-Z0-9-]{4,20})/i)
  if (snLabeled) fields.serialNumber = snLabeled[1].toUpperCase()
  const pnLabeled = text.match(/P\/?N\s*[:#]?\s*([A-Z0-9-]{6,20})/i)
  if (pnLabeled) fields.productNumber = pnLabeled[1].toUpperCase()
  const date = text.match(/20\d{2}-[01]\d-[0-3]\d/)
  if (date) fields.mfgDate = date[0]
  if (Object.keys(fields).length > 0) return fields

  // カンマ・セミコロン・空白区切りの複数値（先に判別できたトークンを優先）
  const merged: Partial<Record<ScannedField, string>> = {}
  for (const token of text.split(/[,;\s]+/)) {
    if (!token) continue
    for (const [field, value] of Object.entries(classifyToken(token))) {
      const key = field as ScannedField
      if (!merged[key]) merged[key] = value
    }
  }
  return merged
}
